import React, { useState } from 'react';
import { ExternalLink, Download, AlertCircle, RefreshCw } from 'lucide-react';

const IBKRWebImporter = ({ onDataReceived, onError }) => {
  const [accountId, setAccountId] = useState('U15136454');
  const [loading, setLoading] = useState(false);
  const [restarting, setRestarting] = useState(false);

  // Ortama göre API adresini belirle
  // Hem localhost hem de production için /ibkr-api proxy'sini kullanıyoruz.
  // Production'da Firebase Function (ibkrProxy), Localhost'ta setupProxy.js devreye girer.
  const API_BASE = '/ibkr-api';

  const handleOpenLogin = () => {
    window.open('https://ibkr.datarapor.com/', '_blank', 'width=1000,height=800');
  };

  const handleRestartServer = async () => {
    if (!window.confirm('Sunucuyu yeniden başlatmak istediğinize emin misiniz? Bu işlem yaklaşık 30 saniye sürebilir.')) {
      return;
    }
    
    setRestarting(true);
    try {
        // Kontrol sunucusuna restart isteği at
        // Restart endpoint'i proxy üzerinden çağır (Prod ve Local uyumlu)
        const restartUrl = '/ibkr-api/restart'; 

        await fetch(restartUrl, { method: 'GET' });
        
        // Kullanıcıya bilgi ver
        await new Promise(resolve => setTimeout(resolve, 5000));
        alert('Sunucu yeniden başlatma sinyali gönderildi. Lütfen sayfayı yenileyip 1 dakika sonra tekrar deneyin.');
    } catch (err) {
        console.error(err);
        alert('Komut gönderilemedi');
    } finally {
        setRestarting(false);
    }
  };

  const handleFetchData = async () => {
    if (!accountId) {
      onError('Lütfen Hesap ID giriniz');
      return;
    }

    setLoading(true);
    try {
      // API isteği gönder
      // Localhost: /ibkr-api/portfolio/...
      // Prod: https://ibkr.datarapor.com/v1/api/portfolio/... (Java context path eklenebilir ama şu anki proxy yapısına göre direkt gönderiyoruz)
      // CapRover'daki Python proxy gelen isteği Java'ya iletiyor.
      // Eğer Python proxy path'i değiştirmiyorsa, Java tarafındaki endpoint: /v1/portpolio/... şeklindedir.
      // Localhost proxy'miz: pathRewrite: { '^/ibkr-api': '' } yapıyor. Yani '/ibkr-api/v1/...' -> 'https://ibkr.../v1/...' oluyor.
      // Bu yüzden buraya '/v1/api' eklememiz gerekebilir veya context path zaten buysa gerekmez.
      // Mevcut çalışan kodunuz: fetch(`/ibkr-api/portfolio/${accountId}/positions/0`);
      // Demek ki Java'da endpoint direkt /portfolio/... veya proxy siliyor.
      // Python proxy kodu sadece iletiyor: target = f"{TARGET_URL}{self.path}"
      // Java Gateway endpointleri genelde /v1/api/... veya /v1/portal/... ile başlar.
      // Ancak sizin çalışan kodunuzda bu yoktu, demek ki Java root'ta dinliyor veya setupProxy farklıydı.
      
      // Güvenli yol:
      const url = `${API_BASE}/v1/api/portfolio/${accountId}/positions/0`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Veri çekilemedi. Lütfen sisteme giriş yaptığınızdan emin olun.');
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Geçersiz veri formatı alındı.');
      }

      const formattedData = data.map(item => ({
        symbol: item.ticker || item.contractDesc,
        shares: parseFloat(item.position),
        cost: parseFloat(item.avgCost),
        price: parseFloat(item.mktPrice),
        total: parseFloat(item.mktValue),
        currency: item.currency,
        type: item.assetClass,
        name: item.name,
        exchange: item.listingExchange,
        profitLoss: parseFloat(item.unrealizedPnl)
      }));

      onDataReceived(formattedData);
      
    } catch (error) {
      console.error('IBKR fetch error:', error);
      onError(error.message || 'Veri çekilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700 space-y-6">
      <div className="flex items-start gap-4 p-4 bg-blue-900/20 rounded-lg text-blue-200 text-sm">
        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold mb-1">Sorun Giderme</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>IBKR güvenlik nedeniyle aynı anda sadece tek bir oturuma izin verir.</li>
            <li>Mobil uygulama veya TWS açıksa bu işlem çalışmayabilir, oradan çıkış yapın.</li>
            <li>Eğer sunucu yanıt vermiyorsa, CapRover üzerinden servisi <strong>Restart</strong> etmeniz gerekebilir.</li>
          </ul>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Adım 1: Sisteme Giriş</label>
          <button
            onClick={handleOpenLogin}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors border border-gray-600"
          >
            <ExternalLink className="w-4 h-4" />
            Giriş Ekranını Aç (ibkr.datarapor.com)
          </button>
          
          <button
            onClick={handleRestartServer}
            disabled={restarting}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 bg-red-900/40 hover:bg-red-900/60 text-red-200 rounded-lg transition-colors border border-red-900/50 text-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${restarting ? 'animate-spin' : ''}`} />
            {restarting ? 'Yeniden Başlatılıyor...' : 'Sunucuyu Yeniden Başlat (Kilitlenirse)'}
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Adım 2: Veri Aktarımı</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="Hesap ID (Örn: U1234567)"
              className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 text-gray-200 focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleFetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <span className="animate-spin">⌛</span>
              ) : (
                <Download className="w-4 h-4" />
              )}
              Verileri Çek
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IBKRWebImporter;
